'use client';
import React from 'react';
import Container from '../ui/Container';
import HeroSectionHeading from '../ui/HeroSectionHeading';
import Image from 'next/image';
import SectionHeader from '../ui/SectionHeader';
import OurWorkCard from '../common/OurWorkCard';
import Faqs from '../ui/Faqs';
import { motion } from 'framer-motion';
import { IsolutionsPage } from '../type/solution/solutionType';
import { OurWork } from '../utils/Constant';
import ExploreProject from '../common/ExploreProject';

type IsolutionSubPageType = {
  data: IsolutionsPage;
};

const Solution = ({ data }: IsolutionSubPageType) => {
  return (
    <>
      <div className="-mt-20 overflow-hidden bg-aboutUsHeroBg bg-center bg-no-repeat lg:bg-full">
        <div className="flex h-full min-h-screen items-center justify-center">
          <Container className="relative z-20 flex h-full flex-col items-center justify-end gap-10 py-20 pt-[150px] lg:min-h-screen lg:flex-row lg:justify-between lg:pt-20">
            <HeroSectionHeading
              className="items-start justify-center !gap-10 3xl:max-w-[740px]"
              heading={data?.heroSection?.title}
              description={data?.heroSection?.description}
              btnName={data?.heroSection?.buttonText}
              link={data?.heroSection?.buttonLink}
            />

            <div className="relative aspect-[3/3] h-auto w-full max-w-[300px] md:aspect-[5/4] md:max-w-[450px] 3xl:aspect-[5/6]">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeIn',
                }}
                className="relative h-full w-full"
              >
                <Image
                  src={data?.heroSection?.heroImage}
                  alt={data?.heroSection?.heroImage ? 'Hero Image' : ''}
                  fill
                  priority
                  fetchPriority="high"
                  sizes="(max-width: 640px) 90vw, (max-width: 1024px) 450px, 450px"
                  className="object-contain"
                />
              </motion.div>
            </div>
            <span className="absolute -bottom-[2%] left-1/2 z-0 block -translate-x-1/2 whitespace-pre text-center font-opt text-[45px] font-medium text-primaryRichBlack opacity-10 md:-bottom-[6%] md:text-[100px]">
              {data?.heroSection?.watermark}
            </span>
          </Container>
        </div>
      </div>
      <div
        className={`${data?.ctaSection?.bgClass} relative mb-[60px] bg-cover bg-center bg-no-repeat after:absolute after:inset-0 after:w-full after:bg-black-560-alpha lg:bg-full 3xl:mb-[100px]`}
      >
        <Container className="relative z-30 py-[80px] 3xl:py-[150px]">
          <HeroSectionHeading
            heading={data?.ctaSection?.heading}
            btnName={data?.ctaSection?.buttonText}
            link={data?.ctaSection?.buttonLink}
            headingStyle="text-white"
            className="max-w-[800px]"
            isBtnBackgroundLight={true}
          />
        </Container>
      </div>
      <Container className="mb-[60px] w-full max-w-[1680px] 4xl:mb-[100px]">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:gap-[45px]">
          {OurWork &&
            OurWork.map((item, i) => <OurWorkCard key={i} data={item} />)}
        </div>
      </Container>
      <Container className="mb-[60px] flex flex-col gap-[60px] 3xl:mb-[100px]">
        <SectionHeader
          headingText={data?.healthcareServices?.title}
          descriptionText={data?.healthcareServices?.description}
          className="items-center"
          headingStyle="max-w-[800px] mx-auto text-start md:text-center"
          descriptionStyle="text-start md:text-center"
        />
        <div className="flex flex-col gap-10 xl:flex-row">
          <div className="flex w-full max-w-[600px] flex-col gap-6">
            <h2 className="font-opt text-3xl font-semibold leading-8 text-primary">
              {data?.healthcareServices?.serviceTitle}
            </h2>
            <p className="font-opt text-base font-normal leading-6 text-optDesc">
              {data?.healthcareServices?.serviceDescription}
            </p>
            <ul className="space-y-3">
              {data?.healthcareServices?.services &&
                data?.healthcareServices?.services.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2.5 font-opt text-base font-medium leading-5 text-primary"
                  >
                    <Image
                      src={data?.healthcareServices?.serviceIcon}
                      alt={
                        data?.healthcareServices?.serviceIcon
                          ? 'Service Icon'
                          : ''
                      }
                      width={16}
                      height={16}
                      className="w-2.5"
                    />{' '}
                    {item}
                  </li>
                ))}
            </ul>
          </div>
          <div className="flex w-full gap-5 overflow-x-auto rounded-2xl scrollbar-hide">
            {data?.healthcareServices?.images.map((item, i) => (
              <div
                key={i}
                className="rounded-2x relative aspect-[6/5] h-full w-full min-w-[300px] md:min-w-[340px]"
              >
                <Image
                  src={item}
                  alt={
                    data?.healthcareServices?.images ? 'Healthcare Image' : ''
                  }
                  fill
                  loading="lazy"
                  sizes="(max-width: 640px) 300px, 340px"
                  className="rounded-2xl"
                />
              </div>
            ))}
          </div>
        </div>
      </Container>
      <ExploreProject numberOfCards={6} />
      <Faqs />
    </>
  );
};

export default Solution;
